<script lang="ts">
  import { Action, Getter } from "vuex-facing-decorator";
  import { Component, Vue, Emit, Watch, Prop } from "vue-facing-decorator";
  import { FormTextarea } from "@/components/forms";
  import FormSelect from "@/components/forms/select.vue";
  import { ActivityOrigin } from "@/store/modules/activity.store";
  import { IndexAccountPayload } from "@/store/modules/account.store";
  import { Debounce } from "@/utils/vue-helpers";
  import { ContactView } from "@/types/contacts.js";

  @Component({
    components: {
      FormTextarea,
      FormSelect,
    },
  })
  export default class OriginInput extends Vue {
    @Getter("entityOfficeIds") entityOfficeIds!: number[];

    @Action("contact/getContacts") getContacts!: (payload: { query?: { search: string; perPage: number }; group_id: number }) => Promise<ContactView[]>;
    @Action("account/index") indexAccounts!: (payload: IndexAccountPayload) => Promise<Account[]>;
    @Getter("auth/me") account!: CurrentAccount;

    @Prop() value?: ActivityPatchPayload["origin"];

    activityOrigin = ActivityOrigin;

    payload: ActivityPatchPayload["origin"] = {
      origin: null,
      other_info: "",
      employer_id: undefined,
      surveyor_id: undefined,
      client_id: undefined,
    };

    contacts: ContactView[] = [];

    localEmployers: Account[] = [];

    get originOptions() {
      return Object.values(ActivityOrigin).map((a) => {
        return {
          value: a,
          label: this.$t(`views.dossier.origin.${a}`),
        };
      });
    }

    get employerOptions() {
      return this.localEmployers.map((account) => {
        return {
          value: account.id,
          label: `${account.first_name} ${account.last_name}`,
        };
      });
    }

    get surveyorOptions() {
      return this.localEmployers.map((account) => {
        return {
          value: account.id,
          label: `${account.first_name} ${account.last_name}`,
        };
      });
    }

    get clientOptions() {
      return this.contacts.map((contact) => {
        return {
          value: contact.id,
          label: `${contact.info.first_name} ${contact.info.last_name}`,
        };
      });
    }

    async handleFetchEmployers() {
      if (this.localEmployers.length > 0) {
        return;
      }

      this.localEmployers = await this.indexAccounts({ roles: ["office"], office_ids: this.account.offices, per_page: 99 });
    }

    @Debounce(500)
    async handleFetchContacts(query = "", perPage = 999, loading: any = () => {}) {
      if (this.contacts.length > 0 && !query) {
        return;
      }

      this.contacts = await this.getContacts({
        group_id: this.account.group!,
        query: { search: query, perPage: perPage },
      });

      loading(false);
    }

    handleClientOptionsSearch(query: string, loading: any) {
      if (query.length > 0) {
        loading(true);
      }

      this.handleFetchContacts(query, 30, loading);
    }

    fetchDetails(value: ActivityOrigin | null) {
      if (!value) {
        return;
      }
      if (value === ActivityOrigin.Employer) {
        this.handleFetchEmployers();
      }
      if (value === ActivityOrigin.Surveyor) {
        this.handleFetchEmployers();
      }
      if (value === ActivityOrigin.Client) {
        this.handleFetchContacts();
      }
      if (value === ActivityOrigin.Purchase) {
        this.handleFetchContacts();
      }
    }

    @Emit("input")
    emitInput() {
      return this.payload;
    }

    handleInput() {
      this.$nextTick(() => {
        if (this.payload && this.payload.origin === ActivityOrigin.Employer) {
          this.payload = {
            origin: this.payload.origin,
            employer_id: this.payload.employer_id,
          };
          this.emitInput();
          return;
        }

        if (this.payload && this.payload.origin === ActivityOrigin.Surveyor) {
          this.payload = {
            origin: this.payload.origin,
            surveyor_id: this.payload.surveyor_id,
          };
          this.emitInput();
          return;
        }

        if (this.payload && this.payload.origin === ActivityOrigin.Client) {
          this.payload = {
            origin: this.payload.origin,
            client_id: this.payload.client_id,
          };
          this.emitInput();
          return;
        }

        if (this.payload && this.payload.origin === ActivityOrigin.Other) {
          this.payload = {
            origin: this.payload.origin,
            other_info: this.payload.other_info,
          };
          this.emitInput();
          return;
        }

        // For other cases, emit the current payload
        this.emitInput();
      });
    }

    created() {
      if (this.value) {
        this.payload = this.value;
      }
      this.fetchDetails(this.payload?.origin ?? null);
    }

    @Watch("value", { deep: true, immediate: true })
    handleValueChange(newValue: { origin: ActivityOrigin | null; other_info?: string; employer_id?: number; surveyor_id?: number; client_id?: number }) {
      if (newValue) {
        this.payload = newValue;
      }
    }

    @Watch("payload.origin")
    handleOriginChanged(newValue: ActivityOrigin | null) {
      this.fetchDetails(newValue);
      this.emitInput();
    }
  }
</script>

<template>
  <div class="grid grid-cols-2 gap-4">
    <form-select
      :class="{ 'col-span-2': payload && [activityOrigin.Other, activityOrigin.Sign, null].includes(payload.origin) }"
      v-model="payload.origin"
      name="Origin"
      placeholder="Bron"
      rules="required"
      :clearable="false"
      :searchable="true"
      label="Bron"
      :reduce="(o: any) => o.value"
      :options="originOptions"
      @input="handleInput"
    />
    <template v-if="payload && payload.origin === activityOrigin.Other">
      <form-textarea class="col-span-2" v-model="payload.other_info" @input="handleInput" name="Contact via" placeholder="Contact via"></form-textarea>
    </template>
    <template v-if="payload && payload.origin === activityOrigin.Employer">
      <form-select
        v-model="payload.employer_id"
        name="Medewerker"
        @input="handleInput"
        placeholder="Medewerker"
        rules="required"
        :clearable="false"
        :searchable="true"
        label="Medewerker"
        :reduce="(o: any) => o.value"
        :options="employerOptions"
      ></form-select>
    </template>
    <template v-if="payload && payload.origin === activityOrigin.Surveyor">
      <form-select
        v-model="payload.surveyor_id"
        name="Landmeter"
        @input="handleInput"
        placeholder="Landmeter"
        rules="required"
        :clearable="false"
        :searchable="true"
        label="Landmeter"
        :reduce="(o: any) => o.value"
        :options="surveyorOptions"
      ></form-select>
    </template>
    <template v-if="payload && payload.origin === activityOrigin.Client">
      <form-select
        v-model="payload.client_id"
        name="Klant"
        placeholder="Klant"
        :clearable="false"
        :searchable="true"
        label="Klant"
        :reduce="(o: any) => o.value"
        :options="clientOptions"
        @input="handleInput"
        @search="handleClientOptionsSearch"
      ></form-select>
    </template>
    <template v-if="payload && payload.origin === activityOrigin.Purchase">
      <form-select
        v-model="payload.client_id"
        name="Klant"
        placeholder="Klant"
        :clearable="false"
        :searchable="true"
        label="Klant"
        :reduce="(o: any) => o.value"
        :options="clientOptions"
        @input="handleInput"
        @search="handleClientOptionsSearch"
      ></form-select>
    </template>
  </div>
</template>
